/**
 * The inbox: a filter bar, the ticket list, and the reading pane.
 *
 * Filters combine (Deivydas 2026-10-14): status AND brand AND topic AND date,
 * all in the URL. The server filters, pages (50 at a time) and counts —
 * every status chip is true for the whole scope, not for the rows a browser
 * happened to load (GET /api/tickets/inbox). Selecting a row loads it beside
 * the list; nothing navigates away. /tickets/:id stays the deep link.
 *
 * The rule from the fixtures era survives: **an empty inbox looks empty.**
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Inbox as InboxIcon, TriangleAlert } from 'lucide-react';
import { apiGet } from '../api/client';
import { Badge } from '../components/ui/primitives';
import { DateFilter, FilterSelect, Pager } from '../components/ui/ListControls';
import { TicketPane } from '../components/ticket/TicketPane';
import { ZendeskTicketRef } from '../components/ticket/ZendeskTicketRef';
import { useBrands } from '../api/admin';
import { cn } from '../lib/cn';
import { INTENT_OPTIONS } from '../lib/inbox/intent-labels';
import { ticketAge, toInboxRows, type ApiTicket, type RowStatus } from '../lib/inbox/rows';
import { formatDateTime } from '../lib/format';
import { pageFromParams, rangeFromParams, rangeQuery, toQuery, withFilters } from '../lib/filters/list-params';

const STATUS: Record<RowStatus, { label: string; tone: 'fact' | 'held' | 'stop' | 'neutral' }> = {
  needs_approval: { label: 'Needs approval', tone: 'held' },
  drafted: { label: 'Drafted', tone: 'neutral' },
  escalated: { label: 'Escalated', tone: 'stop' },
  auto_closed: { label: 'Closed', tone: 'neutral' },
  awaiting_agent: { label: 'Awaiting agent', tone: 'neutral' },
};

/** Zendesk's own ticket status — what the help desk shows, not what Zoye did. */
const ZENDESK_STATUS: Record<string, { label: string; tone: 'fact' | 'held' | 'stop' | 'neutral' | 'info' }> = {
  new: { label: 'New', tone: 'info' },
  open: { label: 'Open', tone: 'stop' },
  pending: { label: 'Pending', tone: 'held' },
  hold: { label: 'On-hold', tone: 'neutral' },
  solved: { label: 'Solved', tone: 'fact' },
  closed: { label: 'Closed', tone: 'neutral' },
};

const STATUS_CHIPS: Array<{ key: '' | RowStatus; label: string }> = [
  { key: '', label: 'All' },
  { key: 'escalated', label: 'Escalated' },
  { key: 'needs_approval', label: 'Needs approval' },
  { key: 'drafted', label: 'Drafted' },
  { key: 'awaiting_agent', label: 'Awaiting agent' },
  { key: 'auto_closed', label: 'Closed' },
];

interface InboxResponse {
  tickets: ApiTicket[];
  total: number;
  page: number;
  pageSize: number;
  /** Per status for the current brand / topic / date, plus `all`. */
  counts: Record<string, number>;
}

export function InboxPage() {
  const [params, setParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const brands = useBrands();

  const status = params.get('status') ?? '';
  const brandId = params.get('brand') ?? '';
  const intent = params.get('topic') ?? '';
  const range = rangeFromParams(params);
  const page = pageFromParams(params);
  const set = (patch: Record<string, string | null>) => setParams((current) => withFilters(current, patch));

  const query = toQuery({ status, brandId, intent, page, ...rangeQuery(range) });
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['tickets', 'inbox', query],
    queryFn: () => apiGet<InboxResponse>(`/api/tickets/inbox?${query}`),
    // The list stays on screen while the next page or filter loads.
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => toInboxRows(data?.tickets ?? []), [data]);
  const brandOptions = useMemo(
    () => (brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name })),
    [brands.data],
  );
  const filtered = status !== '' || brandId !== '' || intent !== '';

  // Selection follows the visible list: a stale selection falls back to the
  // top row rather than a blank pane.
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0] ?? null;

  if (isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  if (error) {
    return (
      <div className="mx-auto max-w-[700px] px-5 py-12 text-center">
        <TriangleAlert size={26} strokeWidth={1.5} className="mx-auto mb-3 text-stop-700" aria-hidden />
        <p className="text-sm text-ink-700">
          {error instanceof Error ? error.message : 'Could not load tickets.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* filters */}
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-3 py-2">
        <div role="group" aria-label="Status" className="flex flex-wrap items-center gap-1">
          {STATUS_CHIPS.map((chip) => {
            const active = status === chip.key;
            const count = data?.counts[chip.key === '' ? 'all' : chip.key] ?? 0;
            return (
              <button
                key={chip.key || 'all'}
                type="button"
                aria-pressed={active}
                onClick={() => set({ status: chip.key || null })}
                className={cn(
                  'flex h-7 items-center gap-1.5 rounded-sm px-2.5 text-xs font-medium transition-colors duration-fast',
                  active ? 'bg-ink-900 text-paper' : 'bg-surface text-ink-500 shadow-hairline hover:text-ink-900',
                )}
              >
                {chip.label}
                <span className={cn('tabular text-2xs', active ? 'text-paper/70' : 'text-ink-400')}>
                  {count.toLocaleString('en-US')}
                </span>
              </button>
            );
          })}
        </div>
        <span className="mx-1 h-5 w-px bg-line" aria-hidden />
        <FilterSelect
          label="Brand"
          allLabel="All brands"
          value={brandId}
          onChange={(value) => set({ brand: value })}
          options={brandOptions}
        />
        <FilterSelect
          label="Topic"
          allLabel="All topics"
          value={intent}
          onChange={(value) => set({ topic: value })}
          options={INTENT_OPTIONS}
        />
        <DateFilter
          range={range}
          onChange={(next) =>
            set(next ? { range: null, from: next.from, to: next.to } : { range: 'all', from: null, to: null })
          }
        />
        {filtered ? (
          <button
            type="button"
            onClick={() => set({ status: null, brand: null, topic: null })}
            className="text-xs text-ink-400 underline hover:text-ink-900"
          >
            clear filters
          </button>
        ) : null}
        {isFetching ? <span className="text-2xs text-ink-400">updating…</span> : null}
      </div>

      <div className="flex min-h-0 flex-1">
        {/* ticket list */}
        <div className="flex w-80 shrink-0 flex-col border-r border-line bg-surface">
          <div className="min-h-0 flex-1 overflow-y-auto">
            {(data?.counts.all ?? 0) === 0 && !filtered && range === null ? (
              <EmptyInbox />
            ) : rows.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-ink-400">Nothing matches these filters.</p>
            ) : (
              <ul>
                {rows.map((row) => {
                  const active = selected?.id === row.id;
                  return (
                    <li key={row.id}>
                      {/* A div, not a <button>: the ticket number inside is a
                          link to Zendesk, and a link inside a button is invalid. */}
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedId(row.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedId(row.id);
                          }
                        }}
                        aria-current={active ? 'true' : undefined}
                        className={cn(
                          'block w-full cursor-pointer border-b border-line px-3.5 py-2.5 text-left transition-colors duration-fast',
                          active
                            ? 'bg-accent-50 shadow-[inset_3px_0_0_var(--accent-600)]'
                            : 'hover:bg-surface-sunken',
                        )}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-xs font-semibold text-ink-900">
                            {row.customer ?? 'Unknown sender'}
                          </span>
                          <ZendeskTicketRef
                            externalId={row.externalId}
                            className="shrink-0 font-mono text-2xs text-ink-400"
                          />
                        </div>
                        <div className="mt-0.5 truncate text-xs text-ink-500">
                          {row.brand} · {row.intent}
                        </div>
                        <div className="mt-0.5 text-2xs text-ink-400">
                          <span className="tabular">{formatDateTime(row.createdAt)}</span>
                          {' · '}
                          <span className="tabular font-semibold text-ink-700">{ticketAge(row.createdAt)}</span>
                        </div>
                        {row.reason ? (
                          <div className="mt-0.5 truncate text-2xs text-ink-400">{row.reason}</div>
                        ) : null}
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <Badge tone={STATUS[row.status].tone}>{STATUS[row.status].label}</Badge>
                          {row.mode !== 'shadow' ? <Badge tone="accent">{row.mode}</Badge> : null}
                          {row.zendeskStatus ? (
                            <span className="ml-auto flex items-center gap-1 text-2xs text-ink-400" title="Status in Zendesk">
                              Zendesk
                              <Badge tone={ZENDESK_STATUS[row.zendeskStatus]?.tone ?? 'neutral'}>
                                {ZENDESK_STATUS[row.zendeskStatus]?.label ?? row.zendeskStatus}
                              </Badge>
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {data && data.total > 0 ? (
            <Pager
              className="border-t border-line px-3 py-2"
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              onPage={(next) => set({ page: next > 1 ? String(next) : null })}
            />
          ) : null}
        </div>

        {/* reading pane */}
        <div className="min-w-0 flex-1 bg-paper">
          {selected ? (
            <TicketPane key={selected.id} ticketId={selected.id} />
          ) : (
            <p className="px-5 py-12 text-center text-sm text-ink-400">
              {rows.length === 0 ? '' : 'Select a ticket to read it here.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyInbox() {
  return (
    <div className="px-4 py-12 text-center">
      <InboxIcon size={28} strokeWidth={1.5} className="mx-auto mb-3 text-ink-300" aria-hidden />
      <p className="text-sm text-ink-700">No tickets yet.</p>
      <p className="mx-auto mt-2 max-w-[280px] text-xs text-ink-400">
        Tickets arrive through the Zendesk webhook. If you expected some, check that
        ZENDESK_WEBHOOK_SIGNING_SECRET is set — without it the endpoint refuses every call — and
        that the worker has Redis attached to drain the queue.
      </p>
    </div>
  );
}
